import { useEffect, useState, useCallback } from "react";
import type { SchoolLiveCoachesResponse } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { RefreshCw } from "lucide-react";

export default function LiveCoachesPage() {
  const schoolIds = useSchoolFilterIds();
  const effectiveSchoolId = schoolIds[0] ?? "";
  const [data, setData] = useState<SchoolLiveCoachesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(() => {
    if (!effectiveSchoolId) return;
    setLoading(true);
    setError(null);
    api
      .getLiveCoaches(effectiveSchoolId)
      .then((res) => {
        setData(res);
        setLastRefresh(new Date());
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load live coaches"))
      .finally(() => setLoading(false));
  }, [effectiveSchoolId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const totalLive = data?.events.reduce((sum, ev) => sum + ev.coaches.length, 0) ?? 0;

  return (
    <div className="flex flex-col gap-6" data-page="live-coaches" data-state={(loading && !data) ? "loading" : error ? "error" : (data && totalLive === 0) ? "empty" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Live Coaches</h1>
          <p className="text-muted-foreground">
            Currently broadcasting coaches grouped by event
            {lastRefresh && (
              <span className="ml-2 text-xs">
                (updated {lastRefresh.toLocaleTimeString()})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SchoolFilter />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-action="refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {(loading && !data) && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {data && totalLive === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <p className="text-muted-foreground">No coaches are currently live</p>
          <p className="text-xs text-muted-foreground">This page auto-refreshes every 30 seconds</p>
        </div>
      )}

      {data && data.events.map((event) => (
        event.coaches.length > 0 && (
          <div key={event.event_id} className="flex flex-col gap-2 rounded-lg border border-border p-4" data-region="event">
            <h2 className="text-lg font-medium">{event.event_name}</h2>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Coach</TableHead>
                    <TableHead>Zone</TableHead>
                    <TableHead>Since</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {event.coaches.map((coach) => (
                    <TableRow key={coach.id} data-item="coach" data-item-id={coach.id}>
                      <TableCell className="font-medium">{coach.coach_name}</TableCell>
                      <TableCell className="text-muted-foreground">{coach.zone_name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(coach.started_at).toLocaleTimeString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      ))}
    </div>
  );
}
